import { gql } from '@apollo/client';

export const MultimediaFragment = gql`
  fragment MultimediaFragment on Multimedia {
    src
    alt
    position
    variant
    active
    _id
  }
`;

export const AddressFragment = gql`
  fragment AddressFragment on Address {
    _id
    country
    city
    state
    municipality
    zip
    address1
    address2
    extra
    active
    location {
      type
      coordinates
      _id
    }
  }
`;

export const CommentFragment = gql`
  fragment CommentFragment on Comment {
    title
    text
    rating
    client
    active
    _id
  }
`;

export const OptionFragment = gql`
  fragment OptionFragment on Option {
    name
    values
    active
    _id
  }
`;

export const TaxFragment = gql`
  fragment TaxFragment on Tax {
    name
    value
    active
    _id
  }
`;

export const CurrencyFragment = gql`
  fragment CurrencyFragment on Currency {
    name
    symbol
    rate
    active
    _id
  }
`;

export const PlanFragment = gql`
  fragment PlanFragment on Plan {
    title
    freeCoupons
    couponPrice
    totalCoupons
    active
    discountCoupons
    _id
    frequencies {
      time
      price
      _id
    }
  }
`;

export const PaymentMethodFragment = gql`
  fragment PaymentMethodFragment on PaymentMethod {
    _id
    title
    commission
    automated
    active
    note
    currency {
      ...CurrencyFragment
    }
    taxes {
      ...TaxFragment
    }
    extraFields {
      name
      value
    }
  }
  ${CurrencyFragment}
  ${TaxFragment}
`;

export const PromoCodeFragment = gql`
  fragment PromoCodeFragment on PromoCode {
    _id
    name
    code
    value
    type
    clients
    products
    expirationDate
    active
  }
`;

export const VariantFragment = gql`
  fragment VariantFragment on Variant {
    title
    sku
    price
    compareAtPrice
    agreedPrice
    product
    active
    _id
    stock
    codesAutogenerated
    isOnline
    option {
      option1
      option2
      option3
      _id
    }
    photos {
      ...MultimediaFragment
    }
  }
  ${MultimediaFragment}
`;

export const CategoryFragment = gql`
  fragment CategoryFragment on Category {
    name
    active
    _id
    position
  }
`;

export const ProductFragment = gql`
  fragment ProductFragment on Product {
    _id
    slug
    title
    priority
    rating
    points
    reviews
    company
    description
    status
    active
    category {
      ...CategoryFragment
    }
    taxes {
      ...TaxFragment
    }
    options {
      ...OptionFragment
    }
    variants {
      ...VariantFragment
    }
    comments {
      ...CommentFragment
    }
  }
  ${TaxFragment}
  ${OptionFragment}
  ${VariantFragment}
  ${CommentFragment}
  ${CategoryFragment}
`;

export const OrderSubscriptionFragment = gql`
  fragment OrderSubscriptionFragment on OrderSubscription {
    title
    plan {
      ...PlanFragment
    }
    client
    price
    discount
    subscription
    shopCart
    order
    active
    _id
  }
  ${PlanFragment}
`;

export const OrderProductFragment = gql`
  fragment OrderProductFragment on OrderProduct {
    title
    company
    sku
    price
    shopCart
    order
    client
    active
    _id
    used
    product {
      ...ProductFragment
    }
    photos {
      ...MultimediaFragment
    }
    variant {
      ...VariantFragment
    }
    taxes {
      ...TaxFragment
    }
    option {
      option1
      option2
      option3
    }
  }
  ${ProductFragment}
  ${MultimediaFragment}
  ${VariantFragment}
  ${TaxFragment}
`;

export const BenefitFragment = gql`
  fragment BenefitFragment on Benefit {
    title
    price
    value
    company
    active
    _id
  }
`;

export const OrderBenefitFragment = gql`
  fragment OrderBenefitFragment on OrderBenefit {
    _id
    title
    value
    client
    company
    shopCart
    order
    active
    benefit {
      ...BenefitFragment
    }
  }
  ${BenefitFragment}
`;

export const ShopCartFragment = gql`
  fragment ShopCartFragment on ShopCart {
    _id
    active
    code
    user
    #subscription
    subscriptions {
      ...OrderSubscriptionFragment
    }
    #order product
    products {
      ...OrderProductFragment
    }
    #order benefit
    benefits {
      ...OrderBenefitFragment
    }
  }
  ${OrderSubscriptionFragment}
  ${OrderProductFragment}
  ${OrderBenefitFragment}
`;

export const CriteriaFragment = gql`
  fragment CriteriaFragment on Criteria {
    _id
    active
    lowerRange
    upperRange
    reward
  }
`;

export const PeriodFragment = gql`
  fragment PeriodFragment on Period {
    name
    target
    criterias {
      ...CriteriaFragment
    }
    unitTime
    current
    active
    startDate
    endDate
    _id
    createdAt
    updatedAt
  }
  ${CriteriaFragment}
`;

export const CompanyDetailsFragment = gql`
  fragment CompanyDetailsFragment on CompanyDetails {
    _id
    structure
    active
    saleChannels
    regions {
      active
      _id
      name
      subRegions
    }
    goals {
      _id
      active
      createdAt
      metric
      periods {
        ...PeriodFragment
      }
      unit
      updatedAt
    }
  }
  ${PeriodFragment}
`;

export const CompanyFragment = gql`
  fragment CompanyFragment on Company {
    _id
    name
    dniType
    dni
    email
    phone
    status
    active
    companyDetails {
      ...CompanyDetailsFragment
    }
    logo {
      ...MultimediaFragment
    }
    addresses {
      ...AddressFragment
    }
    shopCart {
      ...ShopCartFragment
    }
  }
  ${MultimediaFragment}
  ${AddressFragment}
  ${ShopCartFragment}
  ${CompanyDetailsFragment}
`;

export const PaymentInfoFragment = gql`
  fragment PaymentInfoFragment on PaymentInfo {
    paymentMethod {
      ...PaymentMethodFragment
    }
    amount
    metadata
    active
    reference
    _id
  }
  ${PaymentMethodFragment}
`;

export const EventFragment = gql`
  fragment EventFragment on Event {
    title
    message
    active
    _id
  }
`;

export const ClientFragment = gql`
  fragment ClientFragment on Client {
    _id
    user
    addresses {
      ...AddressFragment
    }
    shopCart {
      ...ShopCartFragment
    }
    orders
    benefits
    subscriptions
    promos {
      ...PromoCodeFragment
    }
    wishlist {
      ...ProductFragment
    }
    employeeInfo {
      role
      salesChannel
      region
      subRegion
      relationship
    }
    birthDate
    active
  }
  ${AddressFragment}
  ${ShopCartFragment}
  ${PromoCodeFragment}
  ${ProductFragment}
`;
export const SubscriptionFragment = gql`
  fragment SubscriptionFragment on Subscription {
    _id
    active
    startDate
    expiryDate
    client {
      ...ClientFragment
    }
    plan {
      ...PlanFragment
    }
    frequency {
      time
      price
      _id
    }
  }
`;

export const GroupFragment = gql`
  fragment GroupFragment on Group {
    _id
    company
    name
    pointsToAssign
    src
    users
    active
    createdAt
    updatedAt
  }
`;

export const UserFragment = gql`
  fragment UserFragment on User {
    _id
    firstName
    lastName
    userType
    email
    dni
    dniType
    phone
    active
    privilege
    firstLogin
    discountCoupons
    currentSubscription {
      ...SubscriptionFragment
    }
    photo {
      ...MultimediaFragment
    }
    company {
      ...CompanyFragment
    }
    client {
      ...ClientFragment
    }
  }
  ${MultimediaFragment}
  ${CompanyFragment}
  ${ClientFragment}
  ${SubscriptionFragment}
`;

export const ClientGoalsFragment = gql`
  fragment ClientGoalsFragment on ClientGoals {
    _id
    startDate
    rewarded
    result
    endDate
    updatedAt
    createdAt
    alreadyRewarded
    active
    company {
      ...CompanyFragment
    }
    user {
      ...UserFragment
    }
  }
  ${CriteriaFragment}
  ${CompanyFragment}
  ${UserFragment}
`;

export const BenefitRequestFragment = gql`
  fragment BenefitRequestFragment on BenefitRequest {
    _id
    active
    amountApproved
    amountRequested
    status
    company {
      ...CompanyFragment
    }
    createdAt
    paid
    updatedAt
    user {
      ...UserFragment
    }
  }
  ${CompanyFragment}
  ${UserFragment}
`;

export const CatalogFragment = gql`
  fragment CatalogFragment on CatalogInfo {
    sections {
      category {
        ...CategoryFragment
      }
      count
      products {
        ...ProductFragment
      }
    }
    benefits {
      ...BenefitFragment
    }
  }
  ${CategoryFragment}
  ${ProductFragment}
  ${BenefitFragment}
`;

export const OrderFragment = gql`
  fragment OrderFragment on Order {
    _id
    code
    status
    paymentStatus
    subTotal
    extraFee
    discountTotal
    taxBase
    taxTotal
    commission
    total
    companies
    note
    active
    createdAt
    payment
    user {
      ...UserFragment
    }
    paymentInfo {
      ...PaymentInfoFragment
    }
    history {
      ...EventFragment
    }
    shippingAddress {
      ...AddressFragment
    }
    billingAddress {
      ...AddressFragment
    }
    pickupAddress {
      ...AddressFragment
    }
    promoCodes {
      ...PromoCodeFragment
    }
    products {
      ...OrderProductFragment
    }
    subscriptions {
      ...OrderSubscriptionFragment
    }
    benefitsApplied {
      ...BenefitFragment
    }
  }
  ${AddressFragment}
  ${PromoCodeFragment}
  ${OrderProductFragment}
  ${OrderBenefitFragment}
  ${OrderSubscriptionFragment}
  ${PaymentInfoFragment}
  ${EventFragment}
  ${UserFragment}
`;

export const SettingsFragment = gql`
  fragment SettingsFragment on Settings {
    pickupAddresses
    carouselImages {
      title
      description
      url
      linkType
      link
      _id
    }
    termsAndConditions
    frequentlyAskedQuestions
    supportUrl
    _id
  }
`;

export const PaginationFragment = gql`
  fragment PaginationFragment on PaginationInfo {
    currentPage
    perPage
    pageCount
    itemCount
    hasNextPage
    hasPreviousPage
  }
`;

export const OrderPaymentFragment = gql`
  fragment OrderPaymentFragment on OrderPayment {
    _id
    paymentStatus
    paymentDate
    active
    orderProduct {
      ...OrderProductFragment
    }
    createdAt
    paymentRef {
      updatedAt
      reference
      metadata
      image
      createdAt
      amount
      active
      _id
    }
  }
  ${OrderProductFragment}
`;
